import { ModalBody, ModalFooter, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Option, Select, TextField } from '@main/complykit';
import { useCreateProgramMutation } from '@main/graphql/mutations/CreateProgram.generated';
import {
  GetAvailableFrameworksForOrganizationQuery,
  useGetAvailableFrameworksForOrganizationQuery,
} from '@main/graphql/queries/GetAvailableFrameworksForOrganization.generated';
import { useGetProgramsQuery } from '@main/graphql/queries/GetPrograms.generated';
import { FrameworkType } from '@main/graphql/types.generated';
import { useStableCallback } from '@main/shared/browser';
import { errorToast, successToast, useModal } from '@main/ui';
import { FC, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type CreateProgramFromTemplateModalProps = {
  orgId: string;
};

type FrameworkTypes =
  | GetAvailableFrameworksForOrganizationQuery['templateFrameworks'][number]['__typename']
  | GetAvailableFrameworksForOrganizationQuery['customFrameworks'][number]['__typename'];

const frameworkTypeSchema = z.union([
  z.literal<FrameworkTypes>('template_frameworks'),
  z.literal<FrameworkTypes>('frameworks'),
]);

const createProgramSchema = z.object({
  programName: z.string().min(1, 'Enter a name.').max(200, 'Use 200 characters or fewer.'),
  framework: z.object(
    {
      id: z.string(),
      type: frameworkTypeSchema,
    },
    { message: 'Select a framework.' },
  ),
  description: z.string().max(200, 'Use 200 characters or fewer.'),
});

type CreateProgramFromTemplateForm = z.infer<typeof createProgramSchema>;

export const CreateProgramFromTemplateModal: FC<CreateProgramFromTemplateModalProps> = ({
  orgId,
}) => {
  const { closeModal } = useModal();
  const [createProgram] = useCreateProgramMutation();
  const { refetch } = useGetProgramsQuery({ orgId });

  const { frameworks } = useGetAvailableFrameworksForOrganizationQuery(
    { orgId },
    {
      selectFromResult: ({ data }) => {
        const templateFrameworks = data?.templateFrameworks || [];
        return {
          frameworks: templateFrameworks,
        };
      },
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    control,
  } = useForm<CreateProgramFromTemplateForm>({
    resolver: zodResolver(createProgramSchema),
    defaultValues: {
      framework: undefined,
      programName: '',
      description: '',
    },
    shouldFocusError: false,
  });

  const onClose = useStableCallback(() => {
    closeModal();
    reset();
  });

  const onSubmit: SubmitHandler<CreateProgramFromTemplateForm> = async (data) => {
    try {
      await createProgram({
        programInput: {
          organization_id: orgId,
          framework_id: data.framework.id,
          name: data.programName,
          description: data.description,
          frameworkType:
            data.framework.type === 'template_frameworks'
              ? FrameworkType.Template
              : FrameworkType.Custom,
        },
      }).unwrap();
      successToast('Program successfully created!');
    } catch {
      errorToast('Unable to create program. Try again later.');
    }
    refetch();
    onClose();
  };

  const templateOptions = useMemo(() => {
    return frameworks.map((framework) => ({
      value: framework.id,
      label: framework.name,
      frameworkType: framework.__typename,
    }));
  }, [frameworks]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <ModalBody>
        <Stack spacing={6} pb={4}>
          <Controller
            control={control}
            name="framework"
            render={({ field, fieldState }) => (
              <Select
                isFilterable
                filterLabel="Filter options"
                filterPlaceholder="Filter"
                filterEmptyMessage="No matches"
                emptyMessage="No template frameworks enabled. Enable one in the Frameworks tab."
                ref={field.ref}
                label="Framework"
                description="To create a program using the template framework, enable it in the framework tab."
                name={field.name}
                value={field.value?.id ?? null}
                onChange={(id) => {
                  const option = templateOptions.find((o) => o.value === id);
                  if (option) field.onChange({ id: option.value, type: option.frameworkType });
                }}
                onBlur={field.onBlur}
                isDisabled={field.disabled}
                error={fieldState.error?.message}
                placeholder="Select framework"
                items={templateOptions}
              >
                {(option) => <Option id={option.value}>{option.label}</Option>}
              </Select>
            )}
          />

          <Controller
            control={control}
            name="programName"
            render={({ field, fieldState }) => (
              <TextField
                ref={field.ref}
                label="Name"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter program name"
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <TextField
                ref={field.ref}
                label="Description"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter program description"
                error={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </ModalBody>
      <ModalFooter gap={2}>
        <Button variant="soft" onPress={() => onClose()}>
          Cancel
        </Button>
        <Button isPending={isSubmitting} type="submit">
          Submit
        </Button>
      </ModalFooter>
    </form>
  );
};
